import React, { useState } from 'react';
import { Eye, EyeOff, KeyRound } from 'lucide-react';
import { ILMSModal } from './ILMSModal';
import { ILMSButton } from './ILMSButton';
import { ILMSAlert } from './ILMSAlert';
import { ILMSInput } from './ILMSForm';
import { authApi } from '../services/resources';
import { getApiErrorMessage } from '../services/api';

interface ChangePasswordModalProps {
  open: boolean;
  onClose: () => void;
  onChanged: () => void;
  /** True right after login when must_change_password is set — hides the close button so it can't be skipped. */
  force?: boolean;
}

export function ChangePasswordModal({ open, onClose, onChanged, force = false }: ChangePasswordModalProps) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const reset = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setError(null);
  };

  const handleClose = () => {
    if (force) return;
    reset();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!currentPassword || !newPassword || !confirmPassword) {
      setError('Please fill in all fields.');
      return;
    }
    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('New password and confirmation do not match.');
      return;
    }

    setIsSubmitting(true);
    try {
      await authApi.changePassword(currentPassword, newPassword);
      reset();
      onChanged();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Unable to change password. Please try again.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ILMSModal
      open={open}
      onClose={handleClose}
      title={force ? 'Set a new password to continue' : 'Change Password'}
      size="sm"
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-4" noValidate>
        {force && (
          <ILMSAlert tone="info">
            This account was created with a temporary password. Please set your own password before continuing.
          </ILMSAlert>
        )}
        {error && <ILMSAlert tone="critical">{error}</ILMSAlert>}

        <div className="flex flex-col gap-1">
          <label htmlFor="current-password" className="text-label text-ink-700">
            Current Password
          </label>
          <ILMSInput
            id="current-password"
            type={showPasswords ? 'text' : 'password'}
            autoComplete="current-password"
            autoFocus
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
          />
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="new-password" className="text-label text-ink-700">
            New Password
          </label>
          <ILMSInput
            id="new-password"
            type={showPasswords ? 'text' : 'password'}
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="At least 8 characters"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="confirm-password" className="text-label text-ink-700">
            Confirm New Password
          </label>
          <ILMSInput
            id="confirm-password"
            type={showPasswords ? 'text' : 'password'}
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
          />
        </div>

        <button
          type="button"
          onClick={() => setShowPasswords((v) => !v)}
          className="flex items-center gap-1.5 text-secondary text-ink-500 hover:text-ink-700 self-start"
        >
          {showPasswords ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
          {showPasswords ? 'Hide passwords' : 'Show passwords'}
        </button>

        <div className="flex justify-end gap-2 mt-1">
          {!force && (
            <ILMSButton type="button" variant="ghost" onClick={handleClose}>
              Cancel
            </ILMSButton>
          )}
          <ILMSButton type="submit" icon={<KeyRound />} loading={isSubmitting}>
            Change Password
          </ILMSButton>
        </div>
      </form>
    </ILMSModal>
  );
}
